import { z } from 'zod';
import type { PublicationKind, ReelType } from '../kinds.js';
import type { ContentFormat, FormIssue } from './content.js';
import { mannerChoiceSchema } from './voice.js';

/*
 * Builds (stage 3, spec 6.2 «Стиль и голос», «Сборка», «Результат»): what the
 * Mini App sends at «Собрать» and what it reads back.
 */

export const startBuildSchema = z
  .object({
    draftId: z.number().int().positive(),
    style: z.string().min(1).max(40),
    /** Carousel; ignored for a reel. */
    animated: z.boolean(),
    /** Reel; a carousel always 'none'. */
    music: z.enum(['auto', 'none']),
    /** Reel: which take — without `voiceoverId`, the newest ready take of this source. */
    voice: z.enum(['clone', 'own', 'stock']).optional(),
    /** Reel: the take the person chose on «Озвучка» (spec 9); it must be of this draft, text and ready. */
    voiceoverId: z.number().int().positive().optional(),
    /**
     * Reel without a ready take: the voice and manner chosen on «Озвучка». The server starts
     * (or joins) that take and the build waits for it — closing the Mini App changes nothing.
     */
    take: z
      .object({
        source: z.enum(['clone', 'stock']),
        /** stock: our code of the voice. */
        voice: z.string().min(1).max(32).optional(),
        manner: mannerChoiceSchema,
      })
      .strict()
      .optional(),
  })
  .strict();
export type StartBuildInput = z.infer<typeof startBuildSchema>;

export const ratingInputSchema = z
  .object({ value: z.enum(['up', 'down']), comment: z.string().trim().max(1000).optional() })
  .strict();
export type RatingInput = z.infer<typeof ratingInputSchema>;

/** What a price is made of (spec 8): the base of the kind and surcharges; new options are new codes. */
export const QUOTE_LINE_CODES = ['reel_scenes', 'reel_diagram', 'reel_listicle', 'carousel_animated', 'carousel_static', 'voice_clone'] as const;
export type QuoteLineCode = (typeof QUOTE_LINE_CODES)[number];

export interface QuoteLine {
  code: QuoteLineCode;
  amount: number;
}

export interface BuildQuote {
  price: number;
  free: boolean;
  freeLeft: number;
  /** The parts of `price`; empty for a free rebuild. */
  lines: QuoteLine[];
}

export interface RenderStyleDto {
  id: string;
  name: string;
  about: string;
  accent: string;
  /** Reel: the picture of the type; carousel: the carousel cover. */
  previewUrl: string | null;
  /** Reel: the demo with sound, played on a tap; a carousel has none. */
  previewVideoUrl: string | null;
}

/**
 * GET /renders/examples — the example of a format in this person's default style:
 * what «Формат и текст» shows first. The ideas screen fetches it ahead (the speed-up).
 */
export interface RenderExampleDto {
  kind: PublicationKind;
  imageUrl: string | null;
  videoUrl: string | null;
}

/** One format on «Формат и текст» (spec 4.2, 9): its price, styles and the style it starts on. */
export interface RenderVariantDto {
  kind: PublicationKind;
  /** A new paid result of the kind: a reel the base of its type, a carousel animated. */
  price: number;
  /** Carousel: the static one; null for a reel. */
  staticPrice: number | null;
  styles: RenderStyleDto[];
  /** The last style of this type, the author's niche, the first. */
  defaultStyle: string | null;
}

export interface RenderDraftStateDto {
  draftId: number;
  format: ContentFormat;
  type: ReelType;
  approved: boolean;
  /** The themes of the draft's type, in the passport's order. */
  styles: RenderStyleDto[];
  /** The theme the screen starts on (spec 7.3): the last of this type, the author's niche, the first. */
  defaultStyle: string | null;
  /** Every format the passport offers now, in the order of PUBLICATION_KINDS. */
  variants: RenderVariantDto[];
  /** The format the brain advised for the idea (the lime dot); null without an idea. */
  advisedKind: PublicationKind | null;
  /** The draft's chosen style; null = `defaultStyle` (also when the type lacks the stored one). */
  style: string | null;
  /** Carousel: the chosen kind; null = animated (or the last paid build's kind). */
  animated: boolean | null;
  /** Rewrites for another format still allowed, and the limit itself (setting text_rewrites_per_draft). */
  rewritesLeft: number;
  rewritesLimit: number;
  /**
   * A reel reads `animated` (voiced by the clone), `stockVoice` (a ready-made
   * voice) or `ownVoice` (an own take); a carousel `animated` or `static` (its
   * `ownVoice` and `stockVoice` equal `animated`).
   */
  quote: { animated: BuildQuote; static: BuildQuote; ownVoice: BuildQuote; stockVoice: BuildQuote };
  /** The free rebuilds of one paid result (setting free_rebuilds): «осталось 1 из 2». */
  freeRebuilds: number;
  /** The kind of the draft's last paid build; a carousel starts on it, so a rebuild stays free. */
  lastAnimated: boolean | null;
  balance: number;
  activeJobId: number | null;
  needsSignature: boolean;
}

/** waiting: for its take — «Собрать» was pressed before the voice was ready (0710). */
export type RenderStatusDto = 'waiting' | 'queued' | 'running' | 'done' | 'failed';
export type RenderTellDto = 'text' | 'voice' | 'own_voice' | 'later' | 'sorry';

export interface RenderFileDto {
  kind: 'video' | 'image' | 'archive';
  name: string;
  /** Relative to the API base: '/files/renders/…?e=…&s=…'. */
  url: string;
  downloadUrl: string;
  width?: number;
  height?: number;
  durationMs?: number;
  sizeBytes: number;
}

export interface RenderJobDto {
  id: number;
  draftId: number;
  format: ContentFormat;
  type: ReelType;
  animated: boolean;
  style: string;
  styleName: string;
  status: RenderStatusDto;
  stage: string | null;
  progress: number | null;
  queueAhead: number;
  price: number;
  rebuildNo: number;
  freeRebuildsLeft: number;
  tell: RenderTellDto | null;
  /** Why it failed, our code (e.g. voice_failed: the take of a waiting build failed); null otherwise. */
  error: string | null;
  refunded: number;
  refusal: { code: string; message: string; block: string | null } | null;
  files: RenderFileDto[];
  coverUrl: string | null;
  rating: { value: 'up' | 'down'; comment: string | null } | null;
  createdAt: string;
  finishedAt: string | null;
}

export type StartBuildResult =
  | { kind: 'ok'; job: RenderJobDto }
  | { kind: 'busy'; jobId: number }
  | { kind: 'no_credits'; price: number; balance: number }
  | { kind: 'form'; issues: FormIssue[] }
  /** The take for `take` could not start: the reel's or the day's limit. */
  | { kind: 'voice_limit'; reason: 'per_draft' | 'daily' }
  /** Another voice of this text is being voiced: «Собрать» again when it is done. */
  | { kind: 'voice_busy' }
  | { kind: 'not_found' | 'not_approved' | 'style_unknown' | 'no_voice' | 'no_signature' };
